import React from "react";
import "./HTML_MAIN_CSS.css";

const HTML_WebAPIs = () => {
  return (
    <div className="html-container">
      <h1>HTML Web APIs – Understanding and Using Browser APIs</h1>

      {/* 1. Introduction */}
      <section className="html-section">
        <h2>1. Introduction</h2>
        <p>
          Web APIs (Application Programming Interfaces) allow developers to
          interact with web browsers, servers, and external services using
          JavaScript. HTML works hand-in-hand with these APIs to create dynamic,
          interactive, and modern web applications.
        </p>
        <p>
          A Web API defines a set of methods and properties that developers can
          use to manipulate HTML elements, manage storage, access devices, and
          handle user interactions in a secure and structured way.
        </p>
      </section>

      {/* 2. What is a Web API */}
      <section className="html-section">
        <h2>2. What is a Web API?</h2>
        <p>
          A <strong>Web API</strong> is an interface built into browsers that allows
          developers to perform specific actions without directly accessing system
          hardware or writing low-level code.
        </p>
        <ul>
          <li>
            APIs are collections of prebuilt objects, methods, and events available
            in the browser.
          </li>
          <li>
            They make it possible to build advanced features like geolocation,
            drag-and-drop, file uploads, or offline storage.
          </li>
          <li>
            HTML provides the structure, while JavaScript interacts with APIs to
            manipulate that structure dynamically.
          </li>
        </ul>
      </section>

      {/* 3. Common Browser APIs */}
      <section className="html-section">
        <h2>3. Common Browser Web APIs</h2>
        <table className="styled-table">
          <thead>
            <tr>
              <th>API Name</th>
              <th>Description</th>
              <th>Example</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Document Object Model (DOM)</td>
              <td>Allows JavaScript to manipulate HTML and CSS elements.</td>
              <td><code>document.getElementById("demo").innerHTML = "Hello!";</code></td>
            </tr>
            <tr>
              <td>Geolocation API</td>
              <td>Retrieves the user’s geographical location.</td>
              <td><code>navigator.geolocation.getCurrentPosition(showPosition);</code></td>
            </tr>
            <tr>
              <td>Fetch API</td>
              <td>Makes HTTP requests to servers.</td>
              <td><code>fetch('data.json').then(res =&gt; res.json());</code></td>
            </tr>
            <tr>
              <td>Canvas API</td>
              <td>Draws graphics and animations via JavaScript.</td>
              <td><code>ctx.fillRect(20, 20, 150, 100);</code></td>
            </tr>
            <tr>
              <td>Web Storage API</td>
              <td>Stores data locally in the browser (localStorage, sessionStorage).</td>
              <td><code>localStorage.setItem("name", "Aakash");</code></td>
            </tr>
            <tr>
              <td>Web Workers API</td>
              <td>Runs scripts in the background without blocking the main thread.</td>
              <td><code>new Worker("worker.js");</code></td>
            </tr>
            <tr>
              <td>History API</td>
              <td>Controls browser navigation history.</td>
              <td><code>history.pushState({}, "", "/new-page");</code></td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* 4. Example: Using the Geolocation API */}
      <section className="html-section">
        <h2>4. Example – Geolocation API</h2>
        <p>This API helps you get the user’s current location (with permission).</p>

        <pre className="code-block">
          <code>{`<button onclick="getLocation()">Get My Location</button>
<p id="output"></p>

<script>
  function getLocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(showPosition);
    } else {
      document.getElementById("output").innerHTML = "Geolocation not supported";
    }
  }

  function showPosition(position) {
    document.getElementById("output").innerHTML =
      "Latitude: " + position.coords.latitude +
      "<br>Longitude: " + position.coords.longitude;
  }
</script>`}</code>
        </pre>
      </section>

      {/* 5. Example: Web Storage API */}
      <section className="html-section">
        <h2>5. Example – Web Storage API</h2>
        <p>
          The Web Storage API allows you to store data directly in the browser
          without needing a server or cookies.
        </p>

        <pre className="code-block">
          <code>{`// Save data
localStorage.setItem("username", "JohnDoe");

// Retrieve data
let user = localStorage.getItem("username");

// Remove data
localStorage.removeItem("username");`}</code>
        </pre>
      </section>

      {/* 6. Working with the Fetch API */}
      <section className="html-section">
        <h2>6. Example – Fetch API</h2>
        <p>
          The Fetch API is used to retrieve resources (data, images, JSON files,
          etc.) from the network asynchronously.
        </p>

        <pre className="code-block">
          <code>{`fetch("https://api.example.com/users")
  .then(response => response.json())
  .then(data => console.log(data))
  .catch(error => console.error("Error fetching data:", error));`}</code>
        </pre>
      </section>

      {/* 7. Advantages */}
      <section className="html-section">
        <h2>7. Advantages of Using Web APIs</h2>
        <ul>
          <li>Provide powerful functionality with minimal code.</li>
          <li>Enhance interactivity and responsiveness of web apps.</li>
          <li>Enable access to system features like camera, mic, and sensors.</li>
          <li>Allow seamless integration between web and native-like experiences.</li>
        </ul>
      </section>

      {/* 8. Security Considerations */}
      <section className="html-section">
        <h2>8. Security Considerations</h2>
        <ul>
          <li>APIs require user permission for sensitive data (e.g., location, camera).</li>
          <li>Always use HTTPS for API communication to ensure data integrity.</li>
          <li>Be mindful of CORS (Cross-Origin Resource Sharing) restrictions.</li>
          <li>Validate and sanitize all input/output data.</li>
        </ul>
      </section>

      {/* 9. Common Mistakes */}
      <section className="html-section">
        <h2>9. Common Mistakes When Using Web APIs</h2>
        <ul>
          <li>Not checking for API support before using it.</li>
          <li>Forgetting to handle permission denials or errors.</li>
          <li>Overusing localStorage for sensitive data (security risk).</li>
          <li>Ignoring asynchronous behavior (not using promises correctly).</li>
        </ul>
      </section>

      {/* 10. Summary */}
      <section className="html-section">
        <h2>10. Summary</h2>
        <ul>
          <li>HTML provides the structure, while Web APIs add dynamic functionality.</li>
          <li>APIs like DOM, Fetch, and Geolocation make modern web apps possible.</li>
          <li>Always handle errors, permissions, and security carefully.</li>
          <li>Use feature detection to ensure browser compatibility.</li>
        </ul>
      </section>
    </div>
  );
};

export default HTML_WebAPIs;
